import { useEffect, useState } from 'react';

import { faShieldCheck } from '@awesome.me/kit-f3dc60f0d0/icons/classic/solid';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Trans, useLingui } from '@lingui/react/macro';
import classNames from 'classnames';

import type { ClaimTabData } from './buildClaimTabData';
import { ClaimSuggestedActionsCard } from './ClaimSuggestedActionsCard';
import { ClaimTreatmentCard } from './ClaimTreatmentCard';
import { ClaimTreatmentsTabs } from './ClaimTreatmentsTabs';
import type {
  ClaimTabAppointmentData,
  ClaimTabInsuranceEligibility,
} from './useClaimTabData';

interface ClaimTabPanelProps {
  appointmentData?: ClaimTabAppointmentData;
  data: ClaimTabData;
  fallbackPatientName?: string;
  insuranceEligibility?: ClaimTabInsuranceEligibility;
  isLoading: boolean;
}

export const ClaimTabPanel = ({
  appointmentData,
  data,
  fallbackPatientName,
  insuranceEligibility,
  isLoading,
}: ClaimTabPanelProps) => {
  const { t } = useLingui();
  const [activeTab, setActiveTab] = useState<'all' | 'warnings'>(
    data.warningCount > 0 ? 'warnings' : 'all',
  );

  useEffect(() => {
    if (data.warningCount === 0) {
      setActiveTab('all');
    }
  }, [data.warningCount]);

  const visibleRows =
    activeTab === 'warnings'
      ? data.rows.filter((row) => row.status !== 'clear')
      : data.rows;
  const statusPillClasses = classNames(
    'vai-inline-flex vai-h-9 vai-items-center vai-gap-2 vai-rounded-full vai-px-3 vai-py-2',
    {
      'vai-bg-green-800': insuranceEligibility?.statusVariant === 'success',
      'vai-bg-yellow-900': insuranceEligibility?.statusVariant === 'warning',
      'vai-bg-red-900': insuranceEligibility?.statusVariant === 'error',
      'vai-bg-zinc-800':
        !insuranceEligibility?.statusVariant ||
        insuranceEligibility.statusVariant === 'default',
    },
  );
  const statusTextClasses = classNames(
    'vai-text-xs vai-font-medium vai-leading-none',
    {
      'vai-text-green-200': insuranceEligibility?.statusVariant === 'success',
      'vai-text-yellow-200': insuranceEligibility?.statusVariant === 'warning',
      'vai-text-red-200': insuranceEligibility?.statusVariant === 'error',
      'vai-text-zinc-200':
        !insuranceEligibility?.statusVariant ||
        insuranceEligibility.statusVariant === 'default',
    },
  );

  return (
    <div className="vai-flex vai-h-full vai-flex-col">
      <div className="vai-flex-1 vai-px-3">
        <div className="vai-flex vai-flex-col vai-gap-4 vai-pb-4">
          <div className="vai-flex vai-items-start vai-justify-between vai-gap-3">
            <div>
              <p className="vai-text-sm vai-font-semibold vai-leading-none vai-text-white">
                {appointmentData?.patientName ??
                  fallbackPatientName ??
                  t`Patient`}
              </p>
              {insuranceEligibility?.payerName && (
                <p className="vai-mt-1.5 vai-text-sm vai-leading-5 vai-text-zinc-400">
                  {insuranceEligibility.payerName}
                </p>
              )}
            </div>
            {insuranceEligibility?.statusLabel && (
              <div className={statusPillClasses}>
                <FontAwesomeIcon
                  className={classNames('vai-text-base', statusTextClasses)}
                  icon={faShieldCheck}
                />
                <span className={statusTextClasses}>
                  {insuranceEligibility.statusLabel}
                </span>
              </div>
            )}
          </div>

          {isLoading ? (
            <div className="vai-rounded-[6px] vai-border vai-border-zinc-800 vai-bg-[rgba(41,46,59,0.24)] vai-p-4">
              <p className="vai-text-sm vai-leading-5 vai-text-zinc-400">
                <Trans>Loading claim data...</Trans>
              </p>
            </div>
          ) : (
            <>
              <ClaimSuggestedActionsCard
                suggestedActions={data.suggestedActions}
              />

              <div className="vai-flex vai-flex-col vai-gap-2">
                <p className="vai-text-[12px] vai-uppercase vai-leading-4 vai-tracking-[0.6px] vai-text-zinc-400">
                  <Trans>Scheduled Treatments</Trans>
                </p>
                <ClaimTreatmentsTabs
                  activeTab={activeTab}
                  allCount={data.treatmentCount}
                  onTabChange={setActiveTab}
                  warningCount={data.warningCount}
                />
              </div>

              {visibleRows.length > 0 ? (
                <div className="vai-flex vai-flex-col vai-gap-4">
                  {visibleRows.map((row) => (
                    <ClaimTreatmentCard key={row.treatmentKey} row={row} />
                  ))}
                </div>
              ) : (
                <div className="vai-rounded-[6px] vai-border vai-border-zinc-800 vai-bg-[rgba(41,46,59,0.24)] vai-p-4">
                  <p
                    className={classNames(
                      'vai-text-sm vai-leading-5 vai-text-zinc-400',
                    )}
                  >
                    {data.treatmentCount === 0
                      ? t`No claim data is available for this visit yet.`
                      : t`No treatments currently need review.`}
                  </p>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
};
